"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";

const features = [
  {
    id: "markers",
    icon: "MapPinIcon",
    color: "text-accent-crimson",
    glow: "bg-accent-crimson/10",
    title: "Interactive Markers",
    short: "Point out signatures, damage, certificates, and special details.",
    long: "Point out signatures, damage, certificates, and special details — visitors tap and explore like a museum tour guide. Each marker can contain its own title, description, photos, and even audio. Perfect for highlighting authenticity marks or fascinating details.",
    stat: "Up to 20 markers per item",
  },
  {
    id: "categories",
    icon: "TagIcon",
    color: "text-accent-gold",
    glow: "bg-accent-gold/10",
    title: "103 Smart Categories",
    short: "From vinyl to vintage cars — perfectly organized.",
    long: "Every category is purpose-built with relevant metadata fields. Vinyl records get matrix numbers and pressing details. Sports cards get grading and print run fields. Your collection speaks its own language.",
    stat: "103 curated categories",
  },
  {
    id: "tts",
    icon: "SpeakerWaveIcon",
    color: "text-accent-purple",
    glow: "bg-accent-purple/10",
    title: "Text-to-Speech",
    short: "Bring descriptions to life with natural audio playback.",
    long: "Every item description can be read aloud with high-quality text-to-speech. Perfect for exhibitions, accessibility needs, or simply letting your story be heard. Multiple voice options available.",
    stat: "12 voice options",
  },
  {
    id: "gallery",
    icon: "PhotoIcon",
    color: "text-accent-cyan",
    glow: "bg-accent-cyan/10",
    title: "Multi-Image Gallery",
    short: "Capture every angle, every detail, every moment.",
    long: "Upload unlimited images per item with an intuitive drag-and-drop gallery builder. Set a hero image, arrange the order, and let viewers explore your piece from every perspective. Images are served via CDN for lightning-fast loading.",
    stat: "Unlimited photos per item",
  },
  {
    id: "nfc",
    icon: "DevicePhoneMobileIcon",
    color: "text-accent-emerald",
    glow: "bg-accent-emerald/10",
    title: "NFC + QR Codes",
    short: "Premium NFC tags for seamless tapping, plus QR fallback.",
    long: "Premium NFC tags for seamless tapping, plus QR fallback for older phones — works with every visitor's device. No app required, just tap and explore. Each tag is cryptographically secured to prevent counterfeiting.",
    stat: "Works with all phones",
  },
  {
    id: "templates",
    icon: "PaintBrushIcon",
    color: "text-accent-gold",
    glow: "bg-accent-gold/10",
    title: "5 Viewer Templates",
    short: "Match your collection\'s aesthetic with museum-quality templates.",
    long: "Match your collection\'s aesthetic with 5 museum-quality templates — from vintage scrapbook to modern editorial. Switch anytime without re-cataloging. Each template is mobile-optimized and designed for immersive viewing.",
    stat: "5 stunning templates",
  },
];

export default function FeatureCardsGrid() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el, i) => {
              setTimeout(() => el.classList.add("revealed"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 px-6 md:px-8 bg-bg-primary"
      aria-label="Feature details"
    >
      <div className="max-w-container mx-auto">
        <div className="mb-12 reveal-hidden">
          <span className="font-mono text-[11px] text-accent-crimson uppercase tracking-[0.3em] block mb-4">
            Core Features
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight text-white">
            Built for the
            <br />
            <span className="italic text-gray-400">passionate collector.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feat, i) => (
            <div
              key={feat.id}
              className={`reveal-hidden stagger-${Math.min(i + 1, 5)} rounded-2xl border bg-bg-card p-6 cursor-pointer transition-all duration-500 ${
                expanded === feat.id
                  ? "border-white/20 md:col-span-2" :"border-white/5 hover:border-white/15 card-hover"
              }`}
              onClick={() => setExpanded(expanded === feat.id ? null : feat.id)}
            >
              {/* Icon */}
              <div className={`w-11 h-11 rounded-2xl ${feat.glow} border border-white/10 flex items-center justify-center mb-5`}>
                <Icon name={feat.icon as any} size={20} className={feat.color} />
              </div>

              <h3 className="font-serif text-xl font-light text-white mb-2">{feat.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{feat.short}</p>

              {/* Expanded Content */}
              {expanded === feat.id && (
                <div className="border-t border-white/5 pt-4 mt-2">
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{feat.long}</p>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${feat.glow} ${feat.color}`} />
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${feat.color}`}>
                      {feat.stat}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-1.5 mt-3">
                <span className="text-gray-600 text-xs">
                  {expanded === feat.id ? "Show less" : "Learn more"}
                </span>
                <Icon
                  name={expanded === feat.id ? "ChevronUpIcon" : "ChevronDownIcon"}
                  size={12}
                  className="text-gray-600"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}